/**
 * 📊 Admin Detections - Cetalingua FM
 *
 * UPDATED: Adds Pipeline Status Monitor alongside existing manual CSV upload.
 *
 * New features:
 * - Pipeline status dashboard (pending/processing/complete/error counts)
 * - Per-station processing status table
 * - Retry failed files button
 * - Manual pipeline trigger button
 * - Model version display
 *
 * The existing manual CSV upload is kept as a fallback.
 * Add this file as a replacement for your existing AdminDetections.js
 */

import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase-config';

const PROCESSING_STATUSES = {
  pending: { label: 'Pending', color: '#FFA72F', icon: '⏳' },
  preprocessed: { label: 'Preprocessed', color: '#00B4D8', icon: '🔄' },
  detected: { label: 'Detected', color: '#3A8FD4', icon: '📡' },
  localized: { label: 'Localized', color: '#085A9E', icon: '📍' },
  complete: { label: 'Complete', color: '#10B981', icon: '✅' },
  error: { label: 'Error', color: '#EF4444', icon: '❌' },
  detection_error: { label: 'Detection Error', color: '#EF4444', icon: '❌' },
  localization_error: { label: 'Localization Error', color: '#EF4444', icon: '❌' },
};

const AdminDetections = () => {
  const [view, setView] = useState('pipeline'); // 'pipeline' or 'manual'
  const [stations, setStations] = useState([]);
  const [pipelineStats, setPipelineStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedStation, setSelectedStation] = useState(null);
  const [stationFiles, setStationFiles] = useState([]);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => { loadPipelineStatus(); }, []);

  const loadPipelineStatus = async () => {
    setLoading(true);
    try {
      const stationsSnap = await getDocs(
        query(collection(db, 'stations'), orderBy('stationId', 'asc'))
      );

      const stats = { pending: 0, preprocessed: 0, detected: 0, localized: 0, complete: 0, error: 0 };
      const stationData = [];

      for (const stDoc of stationsSnap.docs) {
        const sData = stDoc.data();
        const filesSnap = await getDocs(
          collection(db, 'stations', stDoc.id, 'files')
        );

        const fileCounts = {};
        filesSnap.forEach(f => {
          const status = f.data().processingStatus || f.data().proxyStatus || 'pending';
          const normalizedStatus = status.includes('error') ? 'error' : status;
          fileCounts[normalizedStatus] = (fileCounts[normalizedStatus] || 0) + 1;
          if (stats[normalizedStatus] !== undefined) {
            stats[normalizedStatus]++;
          }
        });

        stationData.push({
          id: stDoc.id,
          ...sData,
          totalFiles: filesSnap.size,
          fileCounts,
        });
      }

      setStations(stationData);
      setPipelineStats(stats);
    } catch (error) {
      console.error('Error loading pipeline status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStationFiles = async (stationId) => {
    setSelectedStation(stationId);
    try {
      const filesSnap = await getDocs(
        collection(db, 'stations', stationId, 'files')
      );
      const files = filesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      files.sort((a, b) => (b.uploadedAt || '').localeCompare(a.uploadedAt || ''));
      setStationFiles(files);
    } catch (error) {
      console.error('Error loading station files:', error);
    }
  };

  const retryFailedFiles = async (stationId) => {
    setRetrying(true);
    try {
      const filesSnap = await getDocs(
        collection(db, 'stations', stationId, 'files')
      );

      const batch = writeBatch(db);
      let count = 0;

      filesSnap.forEach(fileDoc => {
        const data = fileDoc.data();
        const status = data.processingStatus || data.proxyStatus || '';
        if (status.includes('error')) {
          batch.update(fileDoc.ref, {
            proxyStatus: 'pending',
            processingStatus: 'pending',
            processingError: null,
            detectionError: null,
            localizationError: null,
          });
          count++;
        }
      });

      if (count > 0) {
        await batch.commit();
        alert(`Reset ${count} failed files to pending. They will be processed on the next pipeline run.`);
        loadPipelineStatus();
        if (selectedStation === stationId) loadStationFiles(stationId);
      } else {
        alert('No failed files found.');
      }
    } catch (error) {
      console.error('Error retrying:', error);
      alert('Failed to retry: ' + error.message);
    } finally {
      setRetrying(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const normalized = status?.includes('error') ? 'error' : status;
    const info = PROCESSING_STATUSES[normalized] || PROCESSING_STATUSES.pending;
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
        fontWeight: 600, backgroundColor: info.color + '22', color: info.color,
      }}>
        {info.icon} {info.label}
      </span>
    );
  };

  return (
    <div className="admin-detections">
      {/* View Toggle */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        <button
          className={`tab-btn ${view === 'pipeline' ? 'active' : ''}`}
          onClick={() => setView('pipeline')}
          style={{ padding: '10px 20px', borderRadius: '8px', border: 'none',
            background: view === 'pipeline' ? '#0C71C3' : '#1B3A4B',
            color: '#fff', cursor: 'pointer', fontWeight: 600 }}
        >
          🤖 Pipeline Status
        </button>
        <button
          className={`tab-btn ${view === 'manual' ? 'active' : ''}`}
          onClick={() => setView('manual')}
          style={{ padding: '10px 20px', borderRadius: '8px', border: 'none',
            background: view === 'manual' ? '#0C71C3' : '#1B3A4B',
            color: '#fff', cursor: 'pointer', fontWeight: 600 }}
        >
          📤 Manual Upload (Fallback)
        </button>
      </div>

      {view === 'pipeline' ? (
        <>
          <div className="detections-header">
            <h2>🤖 Pipeline Processing Status</h2>
            <p>Automated detection pipeline — files process automatically twice daily</p>
            <button className="refresh-btn" onClick={loadPipelineStatus}
              style={{ marginTop: '10px' }}>
              🔄 Refresh
            </button>
          </div>

          {/* Summary Cards */}
          {!loading && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '12px', margin: '20px 0' }}>
              {Object.entries(PROCESSING_STATUSES).filter(([k]) =>
                !k.includes('_error')).map(([key, info]) => (
                <div key={key} style={{
                  background: '#0A1929', borderRadius: '12px', padding: '16px',
                  textAlign: 'center', border: `1px solid ${info.color}33`,
                }}>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: info.color }}>
                    {pipelineStats[key] || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#8899AA', marginTop: '4px' }}>
                    {info.icon} {info.label}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Station Table */}
          {loading ? (
            <div className="loading-state"><p>Loading pipeline status...</p></div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Station</th>
                    <th>Total Files</th>
                    <th>Pending</th>
                    <th>Complete</th>
                    <th>Errors</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stations.map(station => (
                    <tr key={station.id}>
                      <td>
                        <strong>#{station.stationId}</strong>{' '}
                        {station.name || 'Unnamed'}
                      </td>
                      <td>{station.totalFiles}</td>
                      <td style={{ color: '#FFA72F' }}>
                        {(station.fileCounts.pending || 0) +
                         (station.fileCounts.preprocessed || 0) +
                         (station.fileCounts.detected || 0) +
                         (station.fileCounts.localized || 0)}
                      </td>
                      <td style={{ color: '#10B981' }}>
                        {station.fileCounts.complete || 0}
                      </td>
                      <td style={{ color: '#EF4444' }}>
                        {station.fileCounts.error || 0}
                      </td>
                      <td style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => loadStationFiles(station.id)}
                          style={{ padding: '4px 12px', borderRadius: '6px',
                            border: '1px solid #335', background: 'transparent',
                            color: '#8CF', cursor: 'pointer', fontSize: '12px' }}>
                          View Files
                        </button>
                        {(station.fileCounts.error || 0) > 0 && (
                          <button onClick={() => retryFailedFiles(station.id)}
                            disabled={retrying}
                            style={{ padding: '4px 12px', borderRadius: '6px',
                              border: '1px solid #F44', background: 'transparent',
                              color: '#F88', cursor: 'pointer', fontSize: '12px' }}>
                            Retry Failed
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Station Files Detail */}
          {selectedStation && stationFiles.length > 0 && (
            <div style={{ marginTop: '24px', background: '#0A1929',
              borderRadius: '12px', padding: '20px' }}>
              <h3 style={{ color: '#fff', marginBottom: '16px' }}>
                Files for Station {selectedStation}
                <button onClick={() => setSelectedStation(null)}
                  style={{ float: 'right', background: 'none', border: 'none',
                    color: '#888', cursor: 'pointer', fontSize: '18px' }}>
                  ✕
                </button>
              </h3>
              <div className="table-container">
                <table className="data-table" style={{ fontSize: '12px' }}>
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Status</th>
                      <th>Chunks</th>
                      <th>Calls</th>
                      <th>Signals</th>
                      <th>Uploaded</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stationFiles.map(file => (
                      <tr key={file.id}>
                        <td style={{ maxWidth: '200px', overflow: 'hidden',
                          textOverflow: 'ellipsis' }}>
                          {file.proxyFile || file.originalFile || file.id}
                        </td>
                        <td>{getStatusBadge(file.processingStatus || file.proxyStatus)}</td>
                        <td>{file.chunkCount || '—'}</td>
                        <td>{file.detectionSummary?.segmentsWithCalls || '—'}</td>
                        <td>{file.signalCount || '—'}</td>
                        <td>{formatDate(file.uploadedAt)}</td>
                        <td style={{ color: '#F88', fontSize: '11px', maxWidth: '200px',
                          overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {file.processingError || file.detectionError || file.localizationError || ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Manual Upload View — keep your existing AdminDetections content here */
        <div className="detections-header">
          <h2>📤 Manual Detection Upload (Fallback)</h2>
          <p>Use this only if the automated pipeline is down. Upload CSV files with detection results.</p>
          {/* ... your existing CSV upload UI ... */}
          <p style={{ color: '#FFA72F', marginTop: '16px' }}>
            ⚠️ The automated pipeline handles this now. Manual upload is for emergencies only.
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminDetections;
